import {Dialog, CircularProgress} from '@mui/material'
import {useSaveProspectList} from '../../../../api/saveProspectList'
import {useDispatch, useSelector} from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import {useState, useEffect, useCallback} from 'react'
import {
  StyledBox,
  StyledButton,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  StyledErrorBox,
  StyledErrorInputLabel,
  StyledIconButton,
  StyledInputLabel,
  StyledTextField
} from './Styles'
import PropTypes from 'prop-types'
import {StyledAsterisk} from '../../../Common/Styles'
import {getAllProspectsData} from '../../../../store/prospectSegment/allProspectsData/allProspectsDataSlice'
import {getProspectsPageNumber} from '../../../../store/pagination/pageNumberSlice'
import {setCurrentSelectedFilter, setFilters} from '../../../../store/askGivePrism/askGivePrismSlice'
import {
  setChatbotData,
  setInterestTagId,
  setMatchedProspectId,
  setMatchedProspectList,
  setMessage
} from '../../../../store/askGivePrism/chatbot/chatbotStreamSlice'

function isJsonString(str) {
  return typeof JSON.parse(str) === 'object'
}

const SaveList = ({open, setOpenDialog, setIsSavingSuccess, refreshProspectList = false}) => {
  const constituentIdList = useSelector((state) => state.saveProspectList.value)
  const [saveProspects, {data, loading, error}] = useSaveProspectList()
  const [errorMessage, setErrorMessage] = useState('')
  const [errorDescriptionMessage, setErrorDescriptionMessage] = useState('')
  const [value, setValue] = useState('')
  const [descriptionValue, setDescriptionValue] = useState('')
  const azureRole = useSelector((state) => state.superUserTenantSlice.azureRole)
  const askGivePrismFilters = useSelector((state) => state.askGivePrism.filters)
  const dispatch = useDispatch()

  const handleSaveProspectList = () => {
    setErrorMessage('')
    setErrorDescriptionMessage('')
    const checkSpecialCharacters = /[^A-Za-z0-9 .,:_&@-]/.test(value)
    const checkDescriptionSpecialCharacters = /[^A-Za-z0-9 .,:_&@-]/.test(descriptionValue)
    if (!value) {
      setErrorMessage('Name cannot be empty')
      return
    } else if (checkSpecialCharacters) {
      setErrorMessage('Name or Description cannot contain special characters')
      return
    } else if (checkDescriptionSpecialCharacters) {
      setErrorDescriptionMessage('Name or Description cannot contain special characters')
      return
    }
    saveProspects({
      variables: {
        listName: value,
        listDescription: descriptionValue,
        constituentIdList,
        ...(azureRole ? {azureRole} : {})
      }
    })
  }

  const handleValueChange = (event) => {
    setValue(event.target.value)
    setDescriptionValue()
    setErrorMessage('')
    setErrorDescriptionMessage('')
  }

  const handleDescriptionChange = (event) => {
    setDescriptionValue(event.target.value)
  }

  const handleClose = useCallback(() => {
    setOpenDialog(false)
  }, [setOpenDialog])

  useEffect(() => {
    if (data?.saveList) {
      handleClose(false)
      setIsSavingSuccess(true)

      if (refreshProspectList && Object.keys(askGivePrismFilters).length > 0) {
        dispatch(setFilters({}))
        dispatch(getAllProspectsData([]))
        dispatch(getProspectsPageNumber(1))
        dispatch(setChatbotData([]))
        dispatch(setMessage(''))
        dispatch(setMatchedProspectId([]))
        dispatch(setInterestTagId([]))
        dispatch(setMatchedProspectList([]))
        dispatch(setCurrentSelectedFilter({}))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, setIsSavingSuccess, data])

  useEffect(() => {
    if (error) {
      const {message} = error
      const formatMessage = message.replaceAll("'", '"')
      return isJsonString(formatMessage) && JSON.parse(formatMessage).status_code === 409
        ? setErrorMessage('List name already exists')
        : setErrorMessage('An error has occurred while saving the list. Please try again later.')
    }
  }, [error])

  return (
    <Dialog
      open={open}
      maxWidth="md"
      sx={{'& .MuiDialog-paper': {backgroundColor: (theme) => theme.palette.common.white, borderRadius: '10px'}}}
    >
      <StyledDialogTitle>
        <StyledIconButton disabled={loading} aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </StyledIconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <StyledBox>
          <StyledInputLabel sx={{display: 'flex'}}>
            Save list as:<StyledAsterisk sx={{marginTop: '-5px'}}>*</StyledAsterisk>
          </StyledInputLabel>
          <StyledTextField
            disabled={loading}
            size="small"
            variant="outlined"
            value={value}
            onChange={handleValueChange}
            error={errorMessage !== ''}
            errorMessage={errorMessage}
          />
        </StyledBox>

        <StyledBox marginTop={'10px'}>
          <StyledInputLabel sx={{marginRight: '24px'}}>Description:</StyledInputLabel>
          <StyledTextField
            disabled={loading}
            size="small"
            variant="outlined"
            value={descriptionValue}
            onChange={handleDescriptionChange}
            error={errorDescriptionMessage !== ''}
            errorMessage={errorDescriptionMessage}
          />
        </StyledBox>
        {(errorMessage || errorDescriptionMessage) && (
          <StyledErrorBox>
            <StyledErrorInputLabel>{errorMessage || errorDescriptionMessage}</StyledErrorInputLabel>
          </StyledErrorBox>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton disabled={loading} onClick={handleSaveProspectList}>
          Save
        </StyledButton>
        {loading && <CircularProgress size="16px" />}
      </StyledDialogActions>
    </Dialog>
  )
}

SaveList.propTypes = {
  open: PropTypes.bool,
  setOpenDialog: PropTypes.func,
  setIsSavingSuccess: PropTypes.func,
  refreshProspectList: PropTypes.bool
}

export default SaveList
